// drawer/SlSubstanceDrawer.tsx
//
// Substance Library — Add / Edit substance drawer (presentational).
//
// ADAPTATION (not a verbatim port) of sl-panel.jsx's SlSubstanceDrawer:
//  · The visual structure, section order, title block, local-state model
//    (s / setS, commit / update / updateStock), identifier round-trip, and
//    the resizable left-edge drag handle are ported from the design mock
//    (sl-panel.jsx 321–706).
//  · Every selector is the REAL SidebarPanel/* component wired to its real
//    prop API — NOT the design-mock window.* shims.
//    Where the mock used a prop the real component lacks, the wiring is
//    adapted (e.g. FlavorFamilyTagSelector single↔multi, TypePill compound/
//    mixture ↔ public.compound/public.food).
//  · Composition (mixtures) uses the REAL ChromatogramFiles / FormulationImport
//    native controlled APIs (A9 props). On the auth-independent dev route they
//    render their idle/empty states (no backend) — acceptable for the
//    visual smoke test; live upload/resolve is Phase B.
//
// CATALOGS via PROP: every search/select/create picker reads its options from
// the `catalogs` prop the parent builds from the live `useInventoryCatalog` /
// `useInventorySuppliers` hooks (see SubstanceLibraryPage). This drawer NEVER
// calls a catalog/supplier hook itself — so a caller that passes NO `catalogs`
// renders empty pickers and fires ZERO network calls.
// Current values resolve to a NAME (never a bare id) via toTypeValue() and the
// flavor-family / location id resolvers below.

import * as React from "react";
import { Icon } from "@/components/Icon/Icon";
import { FormulationImport, type FormulaImport } from "@/components/FormulationImport/FormulationImport";
import { ChromatogramFiles } from "@/pages/CreateFlavorPage/steps/ChromatogramFiles";
import type { Chroma } from "@/utils/types/upload";

import type { SubstanceRow, SubstanceStock, SubstanceType } from "../_model";
import { ReadinessFlag } from "../atoms/ReadinessFlag";
import { blankSubstance } from "./blankSubstance";

import {
  AutoSaveBadge,
  DateField,
  DropArea,
  DropCTA,
  SectionBadge,
  Slash,
  StockToggle,
  SubHead,
} from "./chrome";

import { Collapsible } from "../../SidebarPanel/Collapsible";
import { ComboField } from "../../SidebarPanel/ComboField";
import { CustomTagSelector } from "../../SidebarPanel/CustomTagSelector";
import { DeleteModal } from "../../SidebarPanel/DeleteModal";
import { FieldLabel } from "../../SidebarPanel/FieldLabel";
import { FlavorFamilyTagSelector } from "../../SidebarPanel/FlavorFamilyTagSelector";
import { IdentifierEditor } from "../../SidebarPanel/IdentifierEditor";
import { LocationTagSelector } from "../../SidebarPanel/LocationTagSelector";
import { RawBadge, TypeSelector, type TypeValue } from "../../SidebarPanel/TypeSelector";
import { StorageChip } from "../../SidebarPanel/StorageChip";
import type {
  ComboOption,
  IdentifierEntry,
  RawEntry,
  TagFlavorFamily,
  TagLocation,
  TypeCatalogRow,
} from "../../SidebarPanel/_types";

// ── DrawerCatalogs — the catalogs prop the parent injects ───────────────────
// Mirrors SidebarCatalogs (InventorySidebar). Each TYPE kind carries the
// adapted canonical rows (`items`) plus the org's prior raw values (`raws`).
// TAG kinds (flavorFamilies / locations) and the flat combos (suppliers /
// catalogNos / libraryCodes) follow the same shapes the SidebarPanel
// primitives already consume. customSuggestions is a flat string pool.
export type DrawerTypeCatalog = { items: TypeCatalogRow[]; raws: RawEntry[] };
export type DrawerCatalogs = {
  units: DrawerTypeCatalog;
  currencies: DrawerTypeCatalog;
  storageConditions: DrawerTypeCatalog;
  forms: DrawerTypeCatalog;
  standards: DrawerTypeCatalog;
  claims: DrawerTypeCatalog;
  flavorFamilies: TagFlavorFamily[];
  locations: TagLocation[];
  customSuggestions: string[];
  suppliers: ComboOption[];
  catalogNos: ComboOption[];
  libraryCodes: ComboOption[];
};

const EMPTY_TYPE_CATALOG: DrawerTypeCatalog = { items: [], raws: [] };

/** Resolve a stored string (which may be a catalog id, a slug/code, or a
 *  display label) against a live TYPE catalog into a TypeValue. Returns
 *  `{ id }` when a row matches by id / code / label, `{ raw }` for a non-empty
 *  unmatched string (so it renders as a custom value), or `{}` when empty.
 *  Binding through this guarantees the closed trigger shows a NAME, never a
 *  bare id — TypeSelector resolves `value.id` against the same catalog. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function toTypeValue(
  stored: string | undefined,
  catalog: TypeCatalogRow[],
): TypeValue {
  const v = (stored ?? "").trim();
  if (!v) return {};
  const lower = v.toLowerCase();
  const hit = catalog.find(
    (r) =>
      r.id === v ||
      (r.code ?? "").toLowerCase() === lower ||
      (r.label ?? "").toLowerCase() === lower,
  );
  if (hit) return { id: hit.id };
  // Unresolved value: a catalog id that isn't in the (live) catalog — e.g. the
  // catalog is still loading, or the id is archived / out-of-scope. NEVER render
  // a bare uuid: blank it. A genuine user-typed value (non-uuid) still shows raw.
  if (UUID_RE.test(v)) return {};
  return { raw: v };
}

// ── Local extension of SubstanceRow ────────────────────────────────────
// The drawer adds two presentational-only fields not on the canonical model:
//  · extraIds   — identifiers whose kind isn't one of cas/ec/fema/internal
//  · remarks    — free-text notes
//  · stock.standard / stock.claims — spec facets the design captures locally
type ExtraId = { kind: string; value: string };
type DrawerStock = SubstanceStock & {
  standard?: string;
  storage?: string;
  claims?: TypeValue[];
  // Selected currency for the Price field. Held on stock (not local-only
  // component state) so it threads through onUpdate(next) to the persistence
  // hook, where it is written to the InventoryPatch (price_currency_id/_raw).
  currency?: TypeValue;
};
type DrawerSubstance = Omit<SubstanceRow, "stock"> & {
  extraIds?: ExtraId[];
  remarks?: string;
  stock: DrawerStock | null;
};

// A locally-attached document (presentational only — no upload in Phase A).
type DrawerDoc = { id: string; filename: string; sizeKb: number };

const slpMonoInput: React.CSSProperties = {
  width: "100%",
  padding: "7px 10px",
  fontSize: 13,
  border: "1px solid var(--line-strong)",
  borderRadius: 5,
  background: "var(--panel)",
  outline: "none",
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

// Muted, non-interactive variant of slpMonoInput for server-managed read-only
// fields (Added / Last used — created_at/updated_at have no patch field).
const slpReadOnly: React.CSSProperties = {
  ...slpMonoInput,
  border: "1px solid var(--line)",
  background: "var(--bg-sunk)",
  color: "var(--ink-3)",
  cursor: "default",
};

const isoOk = (v: string | null | undefined): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v ?? "");

// ── Identifier round-trip (cas/fema/ec/internal arrays + extraIds) ──────
// Mirrors sl-panel.jsx slpIdentifiers/addIdentifier/removeIdentifier, adapted
// to the REAL IdentifierEditor prop shape ({ id?, kind, value }).
const KIND_FIELD: Record<string, keyof Pick<SubstanceRow, "cas" | "fema" | "ec" | "internal">> = {
  CAS: "cas",
  FEMA: "fema",
  EC: "ec",
  Internal: "internal",
  INT: "internal",
};

function identifiersOf(s: DrawerSubstance): IdentifierEntry[] {
  return [
    ...s.cas.map((value) => ({ kind: "CAS", value })),
    ...s.fema.map((value) => ({ kind: "FEMA", value })),
    ...s.ec.map((value) => ({ kind: "EC", value })),
    ...s.internal.map((value) => ({ kind: "Internal", value })),
    ...(s.extraIds ?? []).map((e) => ({ kind: e.kind, value: e.value })),
  ];
}

// TypePill in the real SidebarPanel keys on substance_ndb_table strings; the
// drawer model keys on "compound"/"mixture". A tiny inline pill mirrors the
// design TypePill but binds directly to the model's SubstanceType so no
// mapping layer is needed.
function TypePill({
  value,
  onChange,
}: {
  value: SubstanceType;
  onChange: (next: SubstanceType) => void;
}) {
  const TYPES: {
    id: SubstanceType;
    label: string;
    icon: "atom" | "sprout";
    soft: string;
    line: string;
    ink: string;
  }[] = [
    { id: "compound", label: "Compound", icon: "atom", soft: "var(--accent-soft)", line: "var(--accent-line)", ink: "var(--accent-ink)" },
    { id: "mixture", label: "Mixture", icon: "sprout", soft: "var(--signal-soft)", line: "#e7c97a", ink: "#7c5813" },
  ];
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 2,
        gap: 0,
        background: "var(--bg-sunk)",
        borderRadius: 999,
        border: "1px solid var(--line)",
        width: "fit-content",
      }}
    >
      {TYPES.map((t) => {
        const sel = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            style={{
              padding: "4px 12px",
              borderRadius: 999,
              fontSize: 11,
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontWeight: 600,
              background: sel ? t.soft : "transparent",
              color: sel ? t.ink : "var(--ink-3)",
              border: `1px solid ${sel ? t.line : "transparent"}`,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              cursor: "pointer",
            }}
          >
            <Icon name={t.icon} size={11} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Resizable width ─────────────────────────────────────────────────────
const SLP_MIN_W = 460;
const SLP_MAX_W = 1080;
const SLP_DEFAULT_W = 620;

export type SlSubstanceDrawerProps = {
  mode: "edit" | "add";
  substance: SubstanceRow;
  onClose: () => void;
  onUpdate?: (id: string, next: SubstanceRow) => void;
  onCreate?: (next: SubstanceRow) => void;
  onDelete?: (id: string) => void;
  /** Add-mode CODE suggestion (Task B3). Debounced and called with the current
   *  identifiers whenever they change; the live page resolves a CODE and the
   *  suggestion lands via a refetch of the list. When omitted (dev/mock route)
   *  no network call fires and the drawer behaves exactly as in Phase A. */
  onResolveCode?: (identifiers: Array<{ kind: string; value: string }>) => void;
  /** Live catalogs that feed every search/select/create picker. Built by the
   *  parent (SubstanceLibraryPage) from useInventoryCatalog / useInventorySuppliers.
   *  Omitted on the auth-independent dev route → all pickers render empty and
   *  the drawer fires zero network calls. */
  catalogs?: DrawerCatalogs;
};

export function SlSubstanceDrawer({
  mode,
  substance,
  onClose,
  onUpdate,
  onCreate,
  onDelete,
  onResolveCode,
  catalogs,
}: SlSubstanceDrawerProps) {
  const isAdd = mode === "add";

  // Catalogs (fall back to empty when absent — dev route). Reading off a single
  // object keeps every picker's source consistent and network-free by default.
  const unitCat = catalogs?.units ?? EMPTY_TYPE_CATALOG;
  const currencyCat = catalogs?.currencies ?? EMPTY_TYPE_CATALOG;
  const storageCat = catalogs?.storageConditions ?? EMPTY_TYPE_CATALOG;
  const formCat = catalogs?.forms ?? EMPTY_TYPE_CATALOG;
  const standardCat = catalogs?.standards ?? EMPTY_TYPE_CATALOG;
  const claimCat = catalogs?.claims ?? EMPTY_TYPE_CATALOG;
  const libraryCodes = catalogs?.libraryCodes ?? [];
  const suppliers = catalogs?.suppliers ?? [];
  const catalogNos = catalogs?.catalogNos ?? [];
  const customSuggestions = catalogs?.customSuggestions ?? [];
  const liveFamilies = catalogs?.flavorFamilies ?? [];
  const liveLocations = catalogs?.locations ?? [];

  const [s, setS] = React.useState<DrawerSubstance>(substance as DrawerSubstance);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [droppedDocs, setDroppedDocs] = React.useState<DrawerDoc[]>([]);
  const docRef = React.useRef<HTMLInputElement | null>(null);

  // ── Composition: real ChromatogramFiles / FormulationImport native state ──
  // Phase A seeds empty; Chroma's real shape differs from the design's mock
  // seed, so we start idle and let the (auth-independent) live components show
  // their empty states. A stable per-substance draftId keeps the upload draft
  // scoped to this substance.
  const draftId = `sl-${substance.id}`;
  const [chromas, setChromas] = React.useState<Chroma[]>([]);
  const [chromaConfigById, setChromaConfigById] = React.useState<Record<string, string>>({});
  const [formImport, setFormImport] = React.useState<FormulaImport | null>(null);

  // ── Resizable drawer width (persisted to localStorage). ──
  const [width, setWidth] = React.useState<number>(() => {
    const v = parseInt(localStorage.getItem("sl_panel_width") ?? "", 10);
    return v && v >= SLP_MIN_W && v <= SLP_MAX_W ? v : SLP_DEFAULT_W;
  });
  const [resizing, setResizing] = React.useState(false);
  const widthRef = React.useRef(width);
  React.useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const startResize = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setResizing(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ew-resize";
    const onMove = (ev: MouseEvent | TouchEvent) => {
      const cx =
        "touches" in ev && ev.touches.length
          ? ev.touches[0]!.clientX
          : (ev as MouseEvent).clientX;
      const next = Math.max(
        SLP_MIN_W,
        Math.min(SLP_MAX_W, Math.min(window.innerWidth - 40, window.innerWidth - cx)),
      );
      setWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
      try {
        localStorage.setItem("sl_panel_width", String(Math.round(widthRef.current)));
      } catch {
        /* ignore */
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
  };

  // Reseed local state when the target substance changes.
  React.useEffect(() => {
    setS(substance as DrawerSubstance);
  }, [substance]);

  // Escape-to-close.
  React.useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // ── Commit helpers ──
  const commit = (next: DrawerSubstance) => {
    setS(next);
    if (!isAdd && onUpdate) onUpdate(next.id, next as SubstanceRow);
  };
  const update = (patch: Partial<DrawerSubstance>) => commit({ ...s, ...patch });
  const updateStock = (patch: Partial<DrawerStock>) => {
    const base: DrawerStock =
      s.stock ?? {
        inStock: true,
        supplier: "",
        catalog: "",
        lot: "",
        size: "",
        price: 0,
        form: "Liquid",
        loc: "",
        expires: "",
      };
    update({ stock: { ...base, ...patch } });
  };
  const st: Partial<DrawerStock> = s.stock ?? {};

  // ── Identifiers ──
  const ids = identifiersOf(s);
  const addIdentifier = ({ kind, value }: { kind: string; value: string }) => {
    const field = KIND_FIELD[kind];
    if (field) update({ [field]: [...s[field], value] } as Partial<DrawerSubstance>);
    else update({ extraIds: [...(s.extraIds ?? []), { kind: kind.toLowerCase(), value }] });
  };
  const removeIdentifier = (i: number) => {
    const ent = ids[i];
    if (!ent) return;
    const field = KIND_FIELD[ent.kind];
    if (field) {
      update({ [field]: s[field].filter((v) => v !== ent.value) } as Partial<DrawerSubstance>);
    } else {
      update({
        extraIds: (s.extraIds ?? []).filter((x) => !(x.kind === ent.kind && x.value === ent.value)),
      });
    }
  };

  // ── Add-mode CODE resolution (Task B3) ──
  // Debounced /resolve against the current identifiers; only fires when the page
  // wires onResolveCode (live route). The dev/mock route omits it → no network.
  const resolveKey = ids.map((i) => `${i.kind}:${i.value}`).join("|");
  React.useEffect(() => {
    if (!isAdd || !onResolveCode) return;
    const idents = ids
      .map((i) => ({ kind: i.kind, value: i.value }))
      .filter((i) => i.value.trim() !== "");
    if (idents.length === 0) return;
    const t = window.setTimeout(() => onResolveCode(idents), 500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveKey, isAdd]);

  const isMixture = s.type === "mixture";

  // ── Composition handlers — keep substance counts/status in sync ──
  const onChromasChange = (list: Chroma[]) => {
    setChromas(list);
    const hasComp = list.length > 0 || (formImport?.rows.length ?? 0) > 0;
    update({
      chrom: list.length,
      status: hasComp ? "READY" : s.status === "READY" ? "NOT_AVAILABLE" : s.status,
    });
  };
  const onFormChange = (next: FormulaImport | null) => {
    setFormImport(next);
    const items = next ? next.rows.length : 0;
    const hasComp = items > 0 || chromas.length > 0;
    update({
      formula: items ? { items } : null,
      status: hasComp ? "READY" : s.status === "READY" ? "NOT_AVAILABLE" : s.status,
    });
  };

  // ── Size parse + unit selector ──
  // The unit is stored INSIDE the size string as its display code ("500 mL")
  // so the persistence hook's parseSize / idOrRaw("unit", …) round-trips it.
  const sizeMatch = String(st.size ?? "").trim().match(/^([\d.]+)\s*(.*)$/);
  const sizeValue = sizeMatch ? sizeMatch[1]! : "";
  const unitCode = sizeMatch ? sizeMatch[2]!.trim() : "";
  // Resolve the stored code to a live catalog row (by code/label/id), else raw.
  const unitValue: TypeValue = toTypeValue(unitCode, unitCat.items);
  const writeSize = (val: string, code: string) =>
    updateStock({ size: `${val}${code ? " " + code : ""}`.trim() });
  const onUnitChange = (next: TypeValue) => {
    const row = next?.id ? unitCat.items.find((u) => u.id === next.id) : null;
    writeSize(sizeValue, row ? row.code : next?.raw ?? "");
  };

  // ── Currency (held on stock so it threads to the persistence hook) ──
  // Stored as { id } (catalog pick) | { raw } (typed). Defaults to {} so an
  // empty drawer shows the placeholder rather than a guessed currency. The
  // persistence hook resolves the stored id against the live currency catalog.
  const currency: TypeValue = st.currency ?? {};

  // ── Claims ──
  const claims: TypeValue[] = st.claims ?? [];
  const addClaim = (next: TypeValue) => {
    if (!next || (!next.id && !next.raw)) return;
    if (next.id && claims.some((c) => c.id === next.id)) return;
    if (next.raw && claims.some((c) => c.raw === next.raw)) return;
    updateStock({ claims: [...claims, next] });
  };
  const removeClaim = (idx: number) =>
    updateStock({ claims: claims.filter((_, i) => i !== idx) });

  // ── Flavor families (single ↔ multi adapter) ──
  // SubstanceRow.family is a single SLUG (e.g. "fruity") but the live catalog
  // and FlavorFamilyTagSelector key on the catalog id (uuid). We:
  //   · bind value = [id-of-live-family-whose-label-lowercased === slug]
  //   · on toggle, store the toggled family's label lowercased back as the slug
  // Storing the slug (not the id) keeps the persistence hook's resolveFamilyId
  // (which matches displayText.toLowerCase() === slug) working unchanged, and
  // re-binding resolves the slug back to the live id each render — so a NAME
  // (chip label) renders, never a bare id. Locally-created families append to
  // `extraFamilies` so they appear immediately before the catalog refetch.
  const [extraFamilies, setExtraFamilies] = React.useState<TagFlavorFamily[]>([]);
  const families = React.useMemo<TagFlavorFamily[]>(
    () => [...liveFamilies, ...extraFamilies],
    [liveFamilies, extraFamilies],
  );
  const familySlug = (f: TagFlavorFamily) => f.label.toLowerCase();
  const selectedFamilyIds = React.useMemo<string[]>(() => {
    if (!s.family) return [];
    const hit = families.find((f) => familySlug(f) === s.family || f.id === s.family);
    return hit ? [hit.id] : [];
  }, [families, s.family]);
  const onToggleFamily = (id: string) => {
    const fam = families.find((f) => f.id === id);
    // Store the slug (label lowercased) so persistence + re-binding both resolve;
    // carry the family's color/label so the row renders it (incl. custom families).
    if (fam) update({ family: familySlug(fam), familyColor: fam.color, familyLabel: fam.label });
    else update({ family: id });
  };
  const onAddFamily = (f: Omit<TagFlavorFamily, "id"> & { id?: string }) => {
    const created: TagFlavorFamily = {
      id: f.id ?? `fam-${Date.now()}`,
      label: f.label,
      color: f.color,
      isDefault: f.isDefault,
    };
    setExtraFamilies((p) => [...p, created]);
    update({ family: familySlug(created), familyColor: created.color, familyLabel: created.label });
  };

  // ── Locations catalog (grouped by storage condition) ──
  // Live locations come from the catalog; locally-created ones append to
  // `extraLocations`. The stored value is the location LABEL (st.loc) so the
  // persistence hook's resolveLocationId(label) round-trips. We bind value by
  // resolving st.loc → the matching live location id, and keep a synthetic
  // "current" row when the stored label isn't (yet) in the catalog so the chip
  // still renders a NAME.
  const [extraLocations, setExtraLocations] = React.useState<TagLocation[]>([]);
  const locations = React.useMemo<TagLocation[]>(() => {
    let list = [...liveLocations, ...extraLocations];
    if (st.loc && !list.some((l) => l.label === st.loc)) {
      list = [
        ...list,
        { id: "loc-current", label: st.loc, storage_condition_id: st.storage ?? null },
      ];
    }
    return list;
  }, [liveLocations, extraLocations, st.loc, st.storage]);
  const locValue = locations.find((l) => st.loc && l.label === st.loc)?.id ?? null;
  const onLocChange = (id: string | null) => {
    const loc = locations.find((l) => l.id === id);
    if (loc) updateStock(loc.storage_condition_id ? { loc: loc.label, storage: loc.storage_condition_id } : { loc: loc.label });
  };
  const onAddLocationLocal = (newLoc: {
    label: string;
    storage_condition_id: string | null;
    storage_condition_raw: string | null;
  }) => {
    const created: TagLocation = {
      id: `loc-new-${Date.now()}`,
      label: newLoc.label,
      storage_condition_id: newLoc.storage_condition_id,
      storage_condition_raw: newLoc.storage_condition_raw,
    };
    setExtraLocations((p) => [...p, created]);
    updateStock(newLoc.storage_condition_id ? { loc: newLoc.label, storage: newLoc.storage_condition_id } : { loc: newLoc.label });
  };

  // ── Expiry tone ──
  const aExpDays =
    st.expires && isoOk(st.expires)
      ? Math.round((new Date(st.expires + "T00:00:00").getTime() - Date.now()) / 86_400_000)
      : null;
  const aExpired = aExpDays !== null && aExpDays < 0;
  const aExpSoon = aExpDays !== null && aExpDays >= 0 && aExpDays <= 90;

  // ── Documents (local, presentational) ──
  const docs = droppedDocs;
  const onDropDocs = (files: File[]) =>
    setDroppedDocs((d) => [
      ...d,
      ...files.map((f) => ({
        id: "d" + Date.now() + Math.random(),
        filename: f.name,
        sizeKb: Math.max(1, Math.round((f.size || 100_000) / 1024)),
      })),
    ]);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(20, 30, 22, 0.2)" }}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="sl-drawer-in"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width,
          maxWidth: "96%",
          background: "var(--panel)",
          borderLeft: "1px solid var(--line)",
          boxShadow: "-12px 0 32px -12px rgba(20,30,22,0.2)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Resize handle — drag the left edge to set the drawer width */}
        <div
          onMouseDown={startResize}
          onTouchStart={startResize}
          title="Drag to resize"
          style={{
            position: "absolute",
            left: -3,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: "ew-resize",
            zIndex: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              width: 3,
              height: 34,
              borderRadius: 999,
              background: resizing ? "var(--brand-green)" : "var(--line-strong)",
              opacity: resizing ? 1 : 0.55,
              transition: "background 120ms, opacity 120ms",
            }}
          />
        </div>

        {/* TOP CHROME */}
        <div
          className="hstack"
          style={{
            padding: "10px 12px 10px 14px",
            borderBottom: "1px solid var(--line)",
            background: "var(--panel)",
            justifyContent: "space-between",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <div className="hstack" style={{ gap: 10 }}>
            {!isAdd && (
              <button
                className="btn xs"
                onClick={() => setConfirmDelete(true)}
                title="Delete this substance"
                style={{ color: "#a14b3a", padding: "5px 7px" }}
                aria-label="Delete substance"
              >
                <Icon name="trash" size={13} />
              </button>
            )}
            <Slash>{isAdd ? "Substance · new" : "Substance · edit"}</Slash>
          </div>
          <div className="hstack" style={{ gap: 8, alignItems: "center" }}>
            {!isAdd && <AutoSaveBadge />}
            {isAdd && (
              <button
                className="btn primary xs"
                onClick={() => onCreate?.(s as SubstanceRow)}
              >
                <Icon name="check" size={11} />
                Save to library
              </button>
            )}
            <button className="btn xs ghost" onClick={onClose} aria-label="Close panel">
              <Icon name="x" size={12} />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div style={{ flex: 1, overflow: "auto", background: "var(--bg)" }}>
          {/* TITLE BLOCK */}
          <div
            style={{
              padding: "18px 24px 16px",
              background: "var(--panel)",
              borderBottom: "1px solid var(--line)",
            }}
          >
            <div className="vstack" style={{ gap: 6, marginBottom: 12 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <FieldLabel
                  infoTip={
                    <>
                      The <strong style={{ color: "#fff" }}>Code</strong> is this substance's
                      short internal handle (e.g. <em>FRU-204</em>). It's how you and the AI
                      refer to it across Palate.
                    </>
                  }
                >
                  Code
                </FieldLabel>
                <ReadinessFlag status={s.status} />
              </div>
              <div style={{ maxWidth: 240 }}>
                <ComboField
                  value={s.code}
                  onChange={(v) => update({ code: v })}
                  options={libraryCodes}
                  placeholder="Pick or type a code…"
                  searchPlaceholder="Search codes, or type a new one…"
                  popoverMinWidth={320}
                />
              </div>
            </div>

            <input
              value={s.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="Substance name…"
              style={{
                width: "100%",
                padding: "4px 0 6px",
                border: "none",
                borderBottom: "1px solid transparent",
                background: "transparent",
                outline: "none",
                fontFamily: "var(--font-display)",
                fontSize: 22,
                fontWeight: 600,
                color: "var(--ink)",
                letterSpacing: "-0.005em",
              }}
              onFocus={(e) => (e.target.style.borderBottomColor = "var(--accent-line)")}
              onBlur={(e) => (e.target.style.borderBottomColor = "transparent")}
            />

            <div className="vstack" style={{ gap: 7, marginTop: 12 }}>
              <FieldLabel
                infoTip={
                  <>
                    Whether N-OS treats this as a single-molecule{" "}
                    <strong style={{ color: "#fff" }}>compound</strong> or a many-compound{" "}
                    <strong style={{ color: "#fff" }}>mixture</strong>. Mixtures need
                    composition data (a chromatogram or formulation) before they can be used.
                  </>
                }
              >
                Considered as
              </FieldLabel>
              <TypePill value={s.type} onChange={(t) => update({ type: t })} />
            </div>
          </div>

          {/* IDENTITY — public identifiers (+ composition for mixtures) */}
          <Collapsible title="Identity" defaultOpen>
            <div className="vstack" style={{ gap: 22 }}>
              {/* Public identifiers */}
              <div className="vstack" style={{ gap: 10 }}>
                <SubHead
                  icon="shield"
                  title="Public identifiers"
                  hint="CAS · EC · FEMA"
                  infoTip={
                    <>
                      Public identifiers (CAS / EC / FEMA){" "}
                      <strong style={{ color: "#fff" }}>cross-link</strong> this substance with
                      regulatory, procurement, and safety &amp; hazards data on the platform.
                      <br />
                      <br />
                      If you don't have an internal <em>Code</em>, that's fine — we'll group
                      inventory items into one library by their public identifiers.
                    </>
                  }
                />
                <IdentifierEditor
                  identifiers={ids}
                  onAdd={addIdentifier}
                  onRemove={removeIdentifier}
                />
              </div>

              {/* Chromatograms (mixtures) — REAL ChromatogramFiles, native API */}
              {isMixture && (
                <div
                  className="vstack"
                  style={{ gap: 12, paddingTop: 18, borderTop: "1px solid var(--line-2)" }}
                >
                  <SubHead
                    icon="chromatogram"
                    title="Chromatograms"
                    hint=".raw · .mzML · .csv"
                    count={chromas.length}
                  />
                  <ChromatogramFiles
                    draftId={draftId}
                    initialChromas={chromas}
                    onChromasChange={onChromasChange}
                    chromaConfigById={chromaConfigById}
                    onChromaConfigByIdChange={setChromaConfigById}
                    hideHeader
                    compactConfig
                    richDropzone
                    hideConfigHint
                    emptyPrompt="Upload chromatograms of this substance"
                  />
                </div>
              )}

              {/* Formulation (mixtures) — REAL FormulationImport, native API */}
              {isMixture && (
                <div
                  className="vstack"
                  style={{ gap: 12, paddingTop: 18, borderTop: "1px solid var(--line-2)" }}
                >
                  <SubHead icon="beaker" title="Formulation" hint=".xlsx · .csv · .tsv" />
                  <FormulationImport
                    value={formImport}
                    onChange={onFormChange}
                    onOpenLibrary={() => {}}
                    compact
                    hideUnresolvedWarning
                  />
                  {/* Phase B: standalone per-substance formulation persistence is
                      deferred to Phase C (user_formula is flavor-scoped today).
                      Imports here update the local composition readiness only —
                      they are NOT written back to the backend. */}
                  <div
                    className="hstack"
                    style={{
                      gap: 8,
                      fontSize: 11.5,
                      color: "var(--ink-4)",
                      lineHeight: 1.5,
                      alignItems: "flex-start",
                    }}
                  >
                    <span style={{ flexShrink: 0, marginTop: 1, color: "var(--ink-3)" }}>
                      <Icon name="info" size={12} />
                    </span>
                    <span>
                      Formulation imports are previewed locally for now — standalone
                      per-substance formulation saving is coming soon.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Collapsible>

          {/* INVENTORY SPEC */}
          <Collapsible
            title="Inventory spec"
            defaultOpen
            action={
              <div style={{ margin: "-6px 0" }}>
                <StockToggle
                  value={!!st.inStock}
                  onChange={(v) => updateStock({ inStock: v })}
                />
              </div>
            }
          >
            <div className="vstack" style={{ gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.4fr 1fr", gap: 10 }}>
                <div className="vstack" style={{ gap: 5 }}>
                  <FieldLabel>Lot no.</FieldLabel>
                  <input
                    value={st.lot ?? ""}
                    onChange={(e) => updateStock({ lot: e.target.value })}
                    placeholder="e.g. L2481-AC"
                    style={slpMonoInput}
                  />
                </div>
                <div className="vstack" style={{ gap: 5 }}>
                  <FieldLabel>Supplier</FieldLabel>
                  {/* Supplier NAME is stored; the persistence hook resolves
                      name→id (mirrors InventorySidebar). */}
                  <ComboField
                    value={st.supplier ?? ""}
                    onChange={(v) => updateStock({ supplier: v })}
                    options={suppliers}
                    placeholder="Pick a supplier…"
                    searchPlaceholder="Search suppliers, or type a new one…"
                    mono={false}
                    popoverMinWidth={280}
                  />
                </div>
                <div className="vstack" style={{ gap: 5 }}>
                  <FieldLabel>Catalog no.</FieldLabel>
                  <ComboField
                    value={st.catalog ?? ""}
                    onChange={(v) => updateStock({ catalog: v })}
                    options={catalogNos}
                    placeholder="Pick or type…"
                    searchPlaceholder="Search catalog nos., or type a new one…"
                    popoverMinWidth={300}
                    popoverAlign="end"
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="vstack" style={{ gap: 6 }}>
                  <FieldLabel>Size</FieldLabel>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 86px", gap: 5 }}>
                    <input
                      value={sizeValue}
                      type="number"
                      onChange={(e) => writeSize(e.target.value, unitCode)}
                      style={slpMonoInput}
                    />
                    <TypeSelector
                      value={unitValue}
                      onChange={onUnitChange}
                      catalog={unitCat.items}
                      rawEntries={unitCat.raws}
                      groupBy="category"
                      placeholder="—"
                      searchPlaceholder="Search units…"
                      popoverMinWidth={280}
                      popoverAlign="end"
                      inputAlign="center"
                      closedText={(r) => r.code}
                      renderHint={(r) => r.category}
                    />
                  </div>
                </div>
                <div className="vstack" style={{ gap: 6 }}>
                  <FieldLabel>Price</FieldLabel>
                  <div style={{ display: "grid", gridTemplateColumns: "72px 1fr", gap: 5 }}>
                    <TypeSelector
                      value={currency}
                      onChange={(n) =>
                        (n?.id || n?.raw) &&
                        updateStock({ currency: n.id ? { id: n.id } : { raw: n.raw! } })
                      }
                      catalog={currencyCat.items}
                      rawEntries={currencyCat.raws}
                      placeholder="—"
                      searchPlaceholder="Search currencies…"
                      popoverMinWidth={300}
                      popoverAlign="start"
                      inputAlign="center"
                      closedText={(r) => r.symbol ?? r.code}
                      renderLabel={(r) => (
                        <>
                          <span
                            style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: 13.5,
                              fontWeight: 700,
                              color: "var(--accent-ink)",
                              width: 22,
                              display: "inline-block",
                              textAlign: "center",
                            }}
                          >
                            {r.symbol}
                          </span>
                          <span
                            style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: 11.5,
                              fontWeight: 600,
                              color: "var(--ink)",
                            }}
                          >
                            {r.code}
                          </span>
                          <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{r.label}</span>
                        </>
                      )}
                    />
                    <input
                      value={st.price ?? ""}
                      type="number"
                      step="0.01"
                      onChange={(e) => updateStock({ price: parseFloat(e.target.value) || 0 })}
                      style={slpMonoInput}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div className="vstack" style={{ gap: 6 }}>
                  <FieldLabel>Standard</FieldLabel>
                  {/* Stored as a catalog id OR a raw display string; toTypeValue
                      resolves either to a NAME-bearing TypeValue. */}
                  <TypeSelector
                    value={toTypeValue(st.standard, standardCat.items)}
                    onChange={(n) => updateStock({ standard: n.id ?? n.raw ?? "" })}
                    catalog={standardCat.items}
                    rawEntries={standardCat.raws}
                    placeholder="Standard…"
                    searchPlaceholder="Search standards, or type a new one…"
                    popoverMinWidth={300}
                  />
                </div>
                <div className="vstack" style={{ gap: 6 }}>
                  <FieldLabel>Form</FieldLabel>
                  <TypeSelector
                    value={toTypeValue(st.form, formCat.items)}
                    onChange={(n) => updateStock({ form: n.id ?? n.raw ?? "" })}
                    catalog={formCat.items}
                    rawEntries={formCat.raws}
                    placeholder="Form…"
                    searchPlaceholder="Search forms, or type a new one…"
                    popoverMinWidth={300}
                    renderLabel={(r) => (
                      <span style={{ fontSize: 12.5, color: "var(--ink)", fontWeight: 500 }}>
                        {r.label}
                      </span>
                    )}
                  />
                </div>
              </div>

              <div className="vstack" style={{ gap: 6 }}>
                <FieldLabel>Claims</FieldLabel>
                {claims.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 4 }}>
                    {claims.map((c, i) => {
                      const row = c.id ? claimCat.items.find((x) => x.id === c.id) : null;
                      return (
                        <span
                          key={i}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "3px 4px 3px 9px",
                            borderRadius: 4,
                            background: row ? "var(--accent-soft)" : "var(--bg-sunk)",
                            color: row ? "var(--accent-ink)" : "var(--ink-2)",
                            border: `1px solid ${row ? "var(--accent-line)" : "var(--line)"}`,
                            fontSize: 11.5,
                            fontWeight: 500,
                          }}
                        >
                          {row ? row.label : <span style={{ fontStyle: "italic" }}>"{c.raw}"</span>}
                          {!row && <RawBadge small />}
                          <button
                            onClick={() => removeClaim(i)}
                            style={{
                              display: "inline-flex",
                              padding: 2,
                              color: "inherit",
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                            }}
                            aria-label="Remove claim"
                          >
                            <Icon name="x" size={9} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <TypeSelector
                  value={{}}
                  onChange={addClaim}
                  catalog={claimCat.items.filter((c) => !claims.some((s2) => s2.id === c.id))}
                  rawEntries={claimCat.raws}
                  placeholder={claims.length === 0 ? "Add a claim…" : "Add another claim…"}
                  searchPlaceholder="Search claims, or type a new one…"
                  renderHint={(r) => (r.scopes ?? []).join(" · ")}
                />
              </div>
            </div>
          </Collapsible>

          {/* STORAGE */}
          <Collapsible title="Storage" defaultOpen={false}>
            <div className="vstack" style={{ gap: 14 }}>
              <div className="vstack" style={{ gap: 6 }}>
                <FieldLabel
                  infoTip={
                    <>
                      The advised <strong style={{ color: "#fff" }}>storage condition</strong>{" "}
                      for this lot — e.g. refrigerated at 2–8 °C. Picking a <em>Location</em>{" "}
                      below sets this to match.
                    </>
                  }
                >
                  Condition
                </FieldLabel>
                {/* Stored as a catalog id OR raw display string; toTypeValue
                    resolves either so the StorageChip shows a NAME, never an id. */}
                <TypeSelector
                  value={toTypeValue(st.storage, storageCat.items)}
                  onChange={(n) => updateStock({ storage: n.id ?? n.raw ?? "" })}
                  catalog={storageCat.items}
                  rawEntries={storageCat.raws}
                  placeholder="Condition…"
                  searchPlaceholder="Search conditions…"
                  renderLabel={(r) => (
                    <>
                      <StorageChip id={r.id} row={r} size="sm" />
                      <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{r.hint}</span>
                    </>
                  )}
                  renderTrigger={(row) =>
                    row ? <StorageChip id={row.id} row={row} size="lg" showHint /> : null
                  }
                />
              </div>
              <div className="vstack" style={{ gap: 6 }}>
                <FieldLabel>Location</FieldLabel>
                <LocationTagSelector
                  value={locValue}
                  onChange={onLocChange}
                  locations={locations}
                  storageConditions={storageCat.items}
                  onAddLocation={onAddLocationLocal}
                />
              </div>
            </div>
          </Collapsible>

          {/* ORGANIZE */}
          <Collapsible title="Organize" defaultOpen={false}>
            <div className="vstack" style={{ gap: 16 }}>
              <div className="vstack" style={{ gap: 8 }}>
                <FieldLabel>Flavor family</FieldLabel>
                {/* single ↔ multi adapter: picking one replaces the single
                    family. value resolves the stored slug → the live family id
                    so a NAME chip renders; onToggle stores the slug back. */}
                <FlavorFamilyTagSelector
                  value={selectedFamilyIds}
                  onToggle={onToggleFamily}
                  families={families}
                  onAddFamily={onAddFamily}
                />
              </div>
              <div className="vstack" style={{ gap: 8 }}>
                <FieldLabel>Custom tags</FieldLabel>
                <CustomTagSelector
                  value={s.tags}
                  onAdd={(t) => update({ tags: [...s.tags, t] })}
                  onRemove={(t) => update({ tags: s.tags.filter((x) => x !== t) })}
                  suggestionPool={customSuggestions}
                />
              </div>
            </div>
          </Collapsible>

          {/* DOCUMENTS */}
          <Collapsible
            title="Documents"
            defaultOpen={false}
            badge={<SectionBadge>{docs.length}</SectionBadge>}
            action={
              <button className="btn xs" onClick={() => docRef.current?.click()}>
                <Icon name="plus" size={10} />
                Attach
              </button>
            }
          >
            <input
              ref={docRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                onDropDocs(Array.from(e.target.files ?? []));
                e.target.value = "";
              }}
            />
            <DropArea onFiles={onDropDocs} label="Drop to attach a document" sublabel="CoA · SDS · TDS">
              <div className="vstack" style={{ gap: 8 }}>
                {docs.length > 0 && (
                  <div className="vstack" style={{ gap: 5 }}>
                    {docs.map((d) => (
                      <div
                        key={d.id}
                        className="hstack"
                        style={{
                          gap: 10,
                          padding: "8px 10px",
                          borderRadius: 6,
                          background: "var(--accent-soft)",
                          border: "1px solid var(--accent-line)",
                        }}
                      >
                        <span style={{ color: "var(--accent-ink)" }}>
                          <Icon name="file" size={13} />
                        </span>
                        <div
                          className="vstack"
                          style={{ flex: 1, gap: 1, minWidth: 0, lineHeight: 1.3 }}
                        >
                          <span style={{ fontSize: 12, fontWeight: 500 }}>{d.filename}</span>
                          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
                            {d.sizeKb} KB · just added
                          </span>
                        </div>
                        <button
                          className="btn xs"
                          onClick={() => setDroppedDocs((arr) => arr.filter((x) => x.id !== d.id))}
                          title="Remove"
                        >
                          <Icon name="trash" size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <DropCTA
                  label="Drop CoA, SDS, TDS, allergen letters…"
                  sublabel="PDF · DOCX · JPG · up to 25 MB"
                  onClick={() => docRef.current?.click()}
                />
              </div>
            </DropArea>
          </Collapsible>

          {/* LIFECYCLE */}
          <Collapsible title="Lifecycle" defaultOpen={false}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="vstack" style={{ gap: 4 }}>
                <FieldLabel>Added</FieldLabel>
                <div style={slpReadOnly} title="Set automatically — not editable">
                  {s.added || "—"}
                </div>
              </div>
              <div className="vstack" style={{ gap: 4 }}>
                <FieldLabel>Last used</FieldLabel>
                <div style={slpReadOnly} title="Set automatically — not editable">
                  {s.lastUsed || "—"}
                </div>
              </div>
              <div className="vstack" style={{ gap: 4, gridColumn: "1 / -1" }}>
                <FieldLabel hint={aExpired ? `${-aExpDays!}d ago` : aExpSoon ? `in ${aExpDays}d` : undefined}>
                  Expires
                </FieldLabel>
                <DateField
                  value={st.expires ?? ""}
                  onChange={(v) => updateStock({ expires: v })}
                  tone={{
                    color: aExpired ? "#7a2018" : aExpSoon ? "#7c5813" : "var(--ink)",
                    borderColor: aExpired ? "#e7b5ad" : aExpSoon ? "#e7c97a" : "var(--line-strong)",
                  }}
                />
              </div>
            </div>
          </Collapsible>

          {/* REMARKS */}
          <section style={{ borderTop: "1px solid var(--line)" }}>
            <header
              style={{
                padding: "11px 24px",
                background: "var(--bg-sunk)",
                borderBottom: "1px solid var(--line)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 10.5,
                  color: "var(--ink)",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                Remarks
              </span>
            </header>
            <div style={{ padding: "16px 24px 22px", background: "var(--panel)" }}>
              <textarea
                value={s.remarks ?? ""}
                onChange={(e) => update({ remarks: e.target.value })}
                placeholder="Notes about this substance — handling, behaviour in formulation, supplier consistency…"
                style={{
                  width: "100%",
                  minHeight: 70,
                  padding: 10,
                  borderRadius: 6,
                  border: "1px solid var(--line-strong)",
                  background: "var(--panel)",
                  fontFamily: "var(--font-sans)",
                  fontSize: 12.5,
                  lineHeight: 1.55,
                  resize: "vertical",
                  outline: "none",
                }}
              />
            </div>
          </section>

          <div style={{ height: 16 }} />
        </div>

        {confirmDelete && (
          <DeleteModal
            itemName={s.name || s.code || "this substance"}
            onCancel={() => setConfirmDelete(false)}
            onConfirm={() => {
              setConfirmDelete(false);
              onDelete?.(s.id);
              onClose();
            }}
          />
        )}
      </aside>
    </div>
  );
}

// Re-export the add-mode factory for convenience (consumers may import either
// from here or directly from ./blankSubstance).
export { blankSubstance };
